import { Component, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowClockwise, Warning } from '@phosphor-icons/react';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Capture toute erreur de rendu pour éviter la page blanche (l'app n'avait aucun
 * ErrorBoundary → la moindre exception de rendu blanchissait toute la SPA).
 * Affiche un message + le détail de l'erreur (utile pour diagnostiquer) et un
 * bouton de rechargement.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Trace en console pour le diagnostic.
    console.error('[ErrorBoundary] Erreur de rendu :', error, info);
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-ground p-6">
        <div className="w-full max-w-lg rounded-goutte border border-rule bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-goutte-sm bg-sante-tint">
            <Warning className="h-6 w-6 text-sante-deep" weight="duotone" />
          </div>
          <h1 className="font-display text-[28px] font-extrabold tracking-[-0.03em] text-ink mb-2">Une erreur est survenue</h1>
          <p className="text-sm text-ink-muted mb-5">
            La page n'a pas pu s'afficher correctement. Vous pouvez la recharger : les données ne sont pas perdues.
          </p>
          {this.state.error?.message && (
            <pre className="text-xs text-[#9A3412] bg-ground border border-rule rounded-goutte-sm p-3 mb-5 overflow-auto text-left">
              {this.state.error.message}
            </pre>
          )}
          <Button onClick={this.handleReload} className="cursor-pointer">
            <ArrowClockwise className="h-4 w-4 mr-2" /> Recharger la page
          </Button>
        </div>
      </div>
    );
  }
}
